import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useUserState } from '../hooks/useUserState';
import { UserProfile, UnlockedAchievement } from '../types/profile';
import { ACHIEVEMENTS, getAchievementById } from '../data/achievements';
import { UnifiedProfileService } from '../services/unifiedProfileService';
import audioFileService from '../services/audioFileService';

interface ProfileScreenProps {
  onBack: () => void;
}

const AVATAR_OPTIONS = ['🍌', '🐵', '🦍', '🎮', '🎯', '🏆', '⭐', '🚀', '🎪', '🎨', '🎭', '🎪'];

export const ProfileScreen: React.FC<ProfileScreenProps> = ({ onBack }) => {
  const { signOut } = useAuth();
  const { userState } = useUserState();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [achievements, setAchievements] = useState<UnlockedAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ pendingSessionsCount: number } | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'achievements' | 'settings'>('profile');

  useEffect(() => {
    loadProfile();
  }, [userState.type, userState.user?.id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      // Load profile using unified service
      const userProfile = await UnifiedProfileService.getProfile(
        userState.type,
        userState.user?.id
      );
      
      setProfile(userProfile);
      setAchievements(userProfile.achievements || []);
      
      // Set sync status for authenticated users
      if (userState.isAuthenticated && (userProfile as any).pendingGameSessions) {
        setSyncStatus({ 
          pendingSessionsCount: (userProfile as any).pendingGameSessions.length 
        });
      } else {
        setSyncStatus(null);
      }
      
      setEditUsername(userProfile.username);
      setEditAvatar(userProfile.avatar);
      
      // Get audio setting from localStorage
      const savedAudioState = localStorage.getItem('audioEnabled');
      const isAudioEnabled = savedAudioState !== 'false'; // Default to true
      setAudioEnabled(isAudioEnabled);
      audioFileService.setEnabled(isAudioEnabled);
      
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      audioFileService.play('click');
      
      // Update profile using unified service
      await UnifiedProfileService.updateProfile(
        { username: editUsername, avatar: editAvatar },
        userState.type,
        userState.user?.id
      );
      
      // Reload profile to reflect changes
      await loadProfile();
      setEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleAudioToggle = () => {
    const newState = !audioEnabled;
    setAudioEnabled(newState);
    audioFileService.setEnabled(newState);
    localStorage.setItem('audioEnabled', newState.toString());
    
    // Play a test sound if enabling audio
    if (newState) {
      audioFileService.play('click');
    }
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    
    try {
      setIsSigningOut(true);
      audioFileService.play('click');
      await signOut();
      // Clear all profile data
      UnifiedProfileService.clearAllData();
      // Navigate away after successful sign out
      onBack();
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  const handleManualSync = async () => {
    if (!userState.canSync || !userState.user) return;
    
    try {
      const syncResult = await UnifiedProfileService.syncWhenOnline(userState.user.id);
      console.log('Manual sync completed:', syncResult.success ? 'success' : 'failed');
      if (syncResult.error) {
        console.error('Sync error:', syncResult.error);
      }
      await loadProfile();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  const handleBackClick = () => {
    audioFileService.play('click');
    onBack();
  };

  const handleTabClick = (tab: 'profile' | 'achievements' | 'settings') => {
    audioFileService.play('click');
    setActiveTab(tab);
  };

  const handleEditClick = () => {
    audioFileService.play('click');
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    audioFileService.play('click');
    setEditMode(false);
    setEditUsername(profile?.username || '');
    setEditAvatar(profile?.avatar || '');
  };

  const handleAvatarClick = (avatar: string) => {
    audioFileService.play('click');
    setEditAvatar(avatar);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <div className="text-6xl mb-4">🍌</div>
        <div className="text-xl font-semibold text-banana-800">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <div className="text-6xl mb-4">❌</div>
        <div className="text-xl font-semibold text-banana-800">Error loading profile</div>
        <button onClick={onBack} className="btn-primary mt-4">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={handleBackClick}
            className="btn-secondary px-4 py-2"
            title="Back to Home"
          >
            ← Back
          </button>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-banana-800">
            👤 Profile & Settings
          </h1>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div
        className="flex justify-center mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <div className="flex bg-white rounded-lg border-2 border-banana-300 overflow-hidden">
          {[
            { id: 'profile', label: '👤 Profile', icon: '👤' },
            { id: 'achievements', label: '🏆 Achievements', icon: '🏆' },
            { id: 'settings', label: '⚙️ Settings', icon: '⚙️' }
          ].map((tab) => (
            <button
              key={tab.id}
              className={`px-6 py-3 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-banana-500 text-white'
                  : 'bg-white text-banana-700 hover:bg-banana-50'
              }`}
              onClick={() => handleTabClick(tab.id as any)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Info Card */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-banana-800">Profile Information</h2>
                {!editMode && (
                  <button
                    onClick={handleEditClick}
                    className="btn-secondary px-4 py-2 text-sm"
                  >
                    ✏️ Edit Profile
                  </button>
                )}
              </div>

              {editMode ? (
                <div className="space-y-4">
                  {/* Edit Username */}
                  <div>
                    <label className="block text-sm font-medium text-banana-800 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      className="w-full px-3 py-2 border border-banana-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-banana-500"
                    />
                  </div>

                  {/* Edit Avatar */}
                  <div>
                    <label className="block text-sm font-medium text-banana-800 mb-2">
                      Choose Avatar
                    </label>
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                      {AVATAR_OPTIONS.map((avatar) => (
                        <button
                          key={avatar}
                          onClick={() => handleAvatarClick(avatar)}
                          className={`p-3 text-2xl rounded-lg border-2 transition-all hover:scale-110 ${
                            editAvatar === avatar
                              ? 'border-banana-500 bg-banana-100'
                              : 'border-banana-200 hover:border-banana-300'
                          }`}
                        >
                          {avatar}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveProfile}
                      className="btn-primary px-4 py-2"
                    >
                      💾 Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="btn-secondary px-4 py-2"
                    >
                      ❌ Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-6xl">{profile.avatar}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-banana-800">{profile.username}</h3>
                      <p className="text-banana-600">
                        {userState.isAuthenticated ? '✅ Authenticated Account' : '👤 Guest Account'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Game Stats Card */}
            <div className="card">
              <h2 className="text-xl font-bold text-banana-800 mb-4">Game Statistics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-banana-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{profile.stats.totalGames}</div>
                  <div className="text-sm text-banana-700">Games Played</div>
                </div>
                <div className="text-center p-4 bg-banana-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{profile.stats.bestScore}</div>
                  <div className="text-sm text-banana-700">Best Score</div>
                </div>
                <div className="text-center p-4 bg-banana-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{profile.stats.longestStreak}</div>
                  <div className="text-sm text-banana-700">Longest Streak</div>
                </div>
                <div className="text-center p-4 bg-banana-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{profile.achievements.length}</div>
                  <div className="text-sm text-banana-700">Achievements</div>
                </div>
              </div>
            </div>

            {/* Sync Status Card */}
            {syncStatus && syncStatus.pendingSessionsCount > 0 && (
              <div className="card bg-blue-50 border-blue-200">
                <h3 className="text-lg font-bold text-blue-800 mb-2">Sync Status</h3>
                <p className="text-blue-700 mb-3">
                  You have {syncStatus.pendingSessionsCount} game session{syncStatus.pendingSessionsCount !== 1 ? 's' : ''} waiting to sync.
                </p>
                {userState.isAuthenticated && (
                  <button
                    onClick={handleManualSync}
                    className="btn-secondary px-4 py-2 text-sm"
                  >
                    🔄 Sync Now
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="space-y-6">
            {/* Achievements Overview */}
            <div className="card">
              <h2 className="text-xl font-bold text-banana-800 mb-4">Your Achievements</h2>
              <div className="mb-6">
                <div className="text-center p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="text-3xl font-bold text-orange-600">{achievements.length}</div>
                  <div className="text-sm text-orange-700">of {ACHIEVEMENTS.length} achievements unlocked</div>
                  <div className="w-full bg-yellow-200 rounded-full h-3 mt-2">
                    <div 
                      className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(achievements.length / ACHIEVEMENTS.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievement Categories */}
            {[
              { id: 'beginner', name: 'Beginner', icon: '🎮' },
              { id: 'consistency', name: 'Consistency', icon: '📅' },
              { id: 'mastery', name: 'Mastery', icon: '⚡' },
              { id: 'explorer', name: 'Explorer', icon: '🗺️' },
              { id: 'challenger', name: 'Challenger', icon: '🚀' }
            ].map((categoryInfo) => {
              const categoryAchievements = ACHIEVEMENTS.filter(a => a.category === categoryInfo.id);
              const unlockedInCategory = achievements.filter(ua => {
                const achievement = getAchievementById(ua.id);
                return achievement?.category === categoryInfo.id;
              });

              return (
                <motion.div
                  key={categoryInfo.id}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-banana-800 flex items-center gap-2">
                      <span>{categoryInfo.icon}</span>
                      {categoryInfo.name}
                    </h3>
                    <span className="text-sm text-banana-600">
                      {unlockedInCategory.length}/{categoryAchievements.length}
                    </span>
                  </div>
                  
                  <div className="grid gap-3">
                    {categoryAchievements.map((achievement) => {
                      const isUnlocked = achievements.some(ua => ua.id === achievement.id);
                      const unlockedAchievement = achievements.find(ua => ua.id === achievement.id);
                      
                      return (
                        <div
                          key={achievement.id}
                          className={`flex items-center p-3 rounded-lg border-2 ${
                            isUnlocked
                              ? 'border-yellow-300 bg-yellow-50'
                              : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className={`text-2xl mr-3 ${isUnlocked ? '' : 'grayscale opacity-50'}`}>
                            {achievement.icon}
                          </div>
                          <div className="flex-grow">
                            <h4 className={`font-bold ${isUnlocked ? 'text-yellow-800' : 'text-gray-500'}`}>
                              {achievement.name}
                            </h4>
                            <p className={`text-sm ${isUnlocked ? 'text-yellow-700' : 'text-gray-400'}`}>
                              {achievement.description}
                            </p>
                            {isUnlocked && unlockedAchievement && (
                              <p className="text-xs text-yellow-600 mt-1">
                                Unlocked {new Date(unlockedAchievement.unlockedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {isUnlocked && (
                            <div className="text-yellow-600 font-bold">
                              ✓
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Audio Settings */}
            <div className="card">
              <h2 className="text-xl font-bold text-banana-800 mb-4 flex items-center gap-2">
                🔊 Audio Settings
              </h2>
              
              <div className="flex items-center justify-between p-4 bg-banana-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-banana-800">Sound Effects</h3>
                  <p className="text-sm text-banana-600">
                    Enable or disable game sound effects
                  </p>
                </div>
                <button
                  onClick={handleAudioToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-banana-500 focus:ring-offset-2 ${
                    audioEnabled ? 'bg-banana-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      audioEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="mt-3 text-sm text-banana-600">
                {audioEnabled ? '🔊 Sound effects enabled' : '🔇 Sound effects muted'}
              </div>
            </div>

            {/* Account Settings */}
            <div className="card">
              <h2 className="text-xl font-bold text-banana-800 mb-4 flex items-center gap-2">
                👤 Account Settings
              </h2>
              
              {userState.isAuthenticated ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">✅</span>
                      <div>
                        <h3 className="font-medium text-green-800">Signed In</h3>
                        <p className="text-sm text-green-600">
                          Your progress is automatically saved and synced
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSignOut}
                    disabled={isSigningOut || !userState.canSignOut}
                    className={`w-full btn-secondary py-3 ${
                      !userState.canSignOut ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={!userState.canSignOut ? 'Sign out requires internet connection' : 'Sign out of your account'}
                  >
                    {isSigningOut ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-banana-600 border-t-transparent rounded-full"></div>
                        Signing out...
                      </span>
                    ) : (
                      'Sign Out'
                    )}
                  </button>
                  
                  {!userState.canSignOut && (
                    <p className="text-sm text-gray-500 text-center">
                      Sign out requires an internet connection
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👤</span>
                    <div>
                      <h3 className="font-medium text-blue-800">
                        {userState.displayName}
                      </h3>
                      <p className="text-sm text-blue-600">
                        {userState.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* About Game */}
            <div className="card">
              <h2 className="text-xl font-bold text-banana-800 mb-4 flex items-center gap-2">
                ℹ️ About the Game
              </h2>
              
              <div className="text-sm text-banana-700 space-y-2">
                <p><strong>How Many Bananas?</strong> - The ultimate size comparison game!</p>
                <p>Test your intuition about length, weight, and volume measurements.</p>
                <div className="pt-2 text-banana-600">
                  <p>🎮 Multiple game modes</p>
                  <p>🏆 Achievement system</p>
                  <p>📱 Offline support</p>
                  <p>🔄 Cross-device sync</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};